import React, { useState, useEffect } from 'react'
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Label, Input, FormGroup, Row, Col, Spinner,
} from 'reactstrap'
import RichTextEditor from './RichTextEditor'
import { editorStateFromBody, bodyFromEditorState } from './minutaRender'
import {
  saveClausula, saveBloco, handlerExiste, normalizaHandler,
} from './minutaApi'
import { notifyError, notifySuccess } from '../../utils/notify'

const BLOCO_TIPOS = [
  { value: 'preambulo', label: 'Preâmbulo' },
  { value: 'qualificacao', label: 'Qualificação das partes' },
  { value: 'considerandos', label: 'Considerandos' },
  { value: 'fecho', label: 'Fecho' },
  { value: 'assinaturas', label: 'Assinaturas' },
  { value: 'testemunhas', label: 'Testemunhas' },
  { value: 'anexo', label: 'Anexo' },
  { value: 'outro', label: 'Outro' },
]

/**
 * Modal de criação/edição de cláusula (kind='clausula') ou bloco (kind='bloco').
 * props:
 *  - isOpen, kind, record (edição), secoes, campos (contrato), onClose, onSaved
 */
const ClausulaModal = ({
  isOpen, kind = 'clausula', record = null, secoes = [], campos = [], onClose, onSaved,
}) => {
  const isBloco = kind === 'bloco'
  const prefix = isBloco ? 'minutaBloco' : 'minutaClaus'

  const [nome, setNome] = useState('')
  const [hand, setHand] = useState('')
  const [handTouched, setHandTouched] = useState(false)
  const [tipo, setTipo] = useState('outro')
  const [secaoId, setSecaoId] = useState('')
  const [editorState, setEditorState] = useState(editorStateFromBody(null))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (record) {
      setNome(record[`${prefix}_nome`] || '')
      setHand(record[`${prefix}_hand`] || '')
      setHandTouched(true)
      setTipo(record.minutaBloco_tipo || 'outro')
      setSecaoId(record.minutaSec_id || '')
      setEditorState(editorStateFromBody(record[`${prefix}_body`]))
    } else {
      setNome('')
      setHand('')
      setHandTouched(false)
      setTipo('outro')
      setSecaoId('')
      setEditorState(editorStateFromBody(null))
    }
  }, [isOpen, record, prefix])

  // Sugere handler a partir do nome enquanto o usuário não editar o campo manualmente
  const handleNomeChange = (value) => {
    setNome(value)
    if (!handTouched) setHand(normalizaHandler(value))
  }

  const handleSave = async () => {
    const nomeTrim = nome.trim()
    const handTrim = normalizaHandler(hand)
    if (!nomeTrim) {
      notifyError('Informe o nome.')
      return
    }
    if (!handTrim) {
      notifyError('Informe um handler (apelido) válido.')
      return
    }

    setSaving(true)
    try {
      const table = isBloco ? 'minutaBloco' : 'minutaClaus'
      const id = record ? record[`${prefix}_id`] : null
      const duplicado = await handlerExiste(table, handTrim, id)
      if (duplicado) {
        notifyError(`O handler "${handTrim}" já está em uso.`)
        setSaving(false)
        return
      }

      const body = bodyFromEditorState(editorState)
      const data = {
        [`${prefix}_nome`]: nomeTrim,
        [`${prefix}_hand`]: handTrim,
        [`${prefix}_body`]: body,
      }
      if (id) data[`${prefix}_id`] = id
      if (isBloco) {
        data.minutaBloco_tipo = tipo
      } else {
        data.minutaSec_id = secaoId ? Number(secaoId) : null
      }

      await (isBloco ? saveBloco(data) : saveClausula(data))
      notifySuccess(record ? 'Registro atualizado.' : 'Registro criado.')
      setSaving(false)
      if (onSaved) onSaved()
    } catch (err) {
      console.log('Erro ao salvar', err)
      notifyError('Erro ao salvar.')
      setSaving(false)
    }
  }

  const titulo = `${record ? 'Editar' : 'Nova'} ${isBloco ? 'bloco' : 'cláusula'}`

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>{titulo}</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={isBloco ? 5 : 6}>
            <FormGroup>
              <Label>Nome</Label>
              <Input value={nome} onChange={(e) => handleNomeChange(e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={isBloco ? 4 : 6}>
            <FormGroup>
              <Label>Handler (apelido único)</Label>
              <Input
                value={hand}
                onChange={(e) => { setHandTouched(true); setHand(e.target.value) }}
                onBlur={(e) => setHand(normalizaHandler(e.target.value))}
              />
            </FormGroup>
          </Col>
          {isBloco && (
            <Col md={3}>
              <FormGroup>
                <Label>Tipo</Label>
                <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  {BLOCO_TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Input>
              </FormGroup>
            </Col>
          )}
          {!isBloco && (
            <Col md={12}>
              <FormGroup>
                <Label>Seção (categorização organizacional)</Label>
                <Input type="select" value={secaoId} onChange={(e) => setSecaoId(e.target.value)}>
                  <option value="">— Sem seção —</option>
                  {secoes.map((s) => (
                    <option key={s.minutaSec_id} value={s.minutaSec_id}>{s.minutaSec_nome}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          )}
        </Row>

        <FormGroup>
          <Label>Corpo</Label>
          <RichTextEditor
            editorState={editorState}
            onEditorStateChange={setEditorState}
            campos={campos}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" /> : 'Salvar'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ClausulaModal
